/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getEnabledRollUps,
    formatProductAnalyticsReportDates,
    formatProductAnalyticsTableDate,
    getChartColorsSubset,
    chartZAxisColor
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './totalcommentsreport_styles';



/* ========== ~~~~~~~~~~ COMMENTING ANALYTICS - TOTAL COMMENTS REPORT ~~~~~~~~~~ ========== */
const TotalCommentsReport = injectSheet(styles)((props) => {

    // props
    const { selectedInterval, setSelectedInterval } = props;

    // redux state
    const { overview, details, status } = useSelector((state) => state.commenting.totalComments);
    const { overview: totalViewsOverview, details: totalViewsDetails, status: totalViewsStatus } = useSelector((state) => state.commenting.totalCommentingViews);
    const [selectedReport, selectReport] = useState('total-comments')
    const [isLoadingReport, loadReport] = useState(false)
    const timerRef = useRef(null);
    const { filters } = useSelector((state) => state.app);
    const  changeType = filters.compareTo;

    // component states
    const [chartLegends, setChartLegends] = useState([]);
    const [totalViewsChartLegends, setTotalViewsChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    // component constants
    const chartTableColors = getChartColorsSubset(2)
    const products = [
        {id: 'rootComments', name: 'Comments', avgName: 'avgRootCommentsPerUniqueUser', color: chartTableColors[0]},
        {id: 'replies', name: 'Replies', avgName: 'avgRepliesPerUniqueUser', color: chartTableColors[1]},
        {id: 'total', name: 'Total', color: '#fff'},
        {id: 'avgCommentsPerUniqueUser', name: 'Avg. Comments per Unique User', color: chartZAxisColor},
    ];
    const totalViewsProducts = [
        {id: 'totalViews', name: 'Total Views', avgName: '', color: chartTableColors[0]}
    ];

    const totalIndex = products.map(product => product.id).indexOf('total');
    const avgIndex = products.map(product => product.id).indexOf('avgCommentsPerUniqueUser');

    const dates = details ? details[selectedInterval] : {};
    const totalViewsDates = totalViewsDetails ? totalViewsDetails[selectedInterval] : {};

    // show aggregate total on load of page
    useEffect(() => {
        if(status === 'fulfilled' && overview) showAggregateTotal();
        if(totalViewsStatus  === 'fulfilled' && totalViewsOverview) showAggregateTotalViews()
    }, [overview, totalViewsOverview]);

    // show aggregate total on chart mouse leave
    useEffect(() => {
        if(showTotal) {
          if(isTotalCommentsSelected) showAggregateTotal();
          else showAggregateTotalViews()
        }
    }, [showTotal]);

    useEffect(() => {
      // Clear the interval when the component unmounts
      return () => clearTimeout(timerRef.current);
    }, []);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if(status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details, 'commenting');
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
        }
    }, [status]);


    const computeTableTotal = (products, checkedOnly = true) => {
        // Compute total depending on the selected products
        const total = products.filter(product => product.id !== 'total' && product.id !== 'avgCommentsPerUniqueUser' && (checkedOnly ? (product.checked === true) : true))
                            .map(product => product.value)
                            .reduce((prev, current) => prev + current, 0);
        return total;
    }

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
            changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        productArr[totalIndex] = {
            ...productArr[totalIndex],
            value: computeTableTotal(productArr)
        }

        // show the average based on the selected product
        if(chartLegends.length > 0) {
            productArr[avgIndex] = {
                ...productArr[avgIndex],
                ...computeAvg({legends: productArr})
            }
        }

        setChartLegends(productArr);    
    };

    const showAggregateTotalViews = () => {
      const productArr = totalViewsProducts.map(product => ({
          ...product,
          value: Object.keys(totalViewsOverview).length > 0 ? totalViewsOverview.period[product.id] : 0,
          changeRate: Object.keys(totalViewsOverview).length > 0 ? totalViewsOverview.period[`${product.id}ChangeRate`] : 0,
          checked: totalViewsChartLegends.length > 0 ? totalViewsChartLegends.filter(legend => legend.id === product.id)[0].checked : true
      }))

     setTotalViewsChartLegends(productArr);    
  };


    const getBarChartKeys = () => isTotalCommentsSelected ? products.map(product => product.name).reverse() : totalViewsProducts.map(product => product.name).reverse();

    const getBarChartColors = () => isTotalCommentsSelected ?  products.map(product => product.color).reverse() : totalViewsProducts.map(product => product.color).reverse() 


    const computeAvg = ({legends, selectedDate, showTotalAvg = true}) => {
        const showAggregate = showTotalAvg && showTotal && !selectedDate;
        const overviewDetail = overview?.period;
        const selectedDetail = details[selectedInterval][selectedDate];
        const updatedLegends = [...legends];


        // if all products are selected, show the overview or details of average comments per unique user
        if(updatedLegends.filter(legend => legend.checked).length === updatedLegends.length) {
            updatedLegends[avgIndex] = {
                ...updatedLegends[avgIndex],
                value: showAggregate ? overviewDetail?.avgCommentsPerUniqueUser : selectedDetail?.avgCommentsPerUniqueUser,
                changeRate: showAggregate ? overviewDetail?.avgCommentsPerUniqueUserRateChange : selectedDetail?.avgCommentsPerUniqueUserRateChange
            }
        } 
        // else if only one is selected, show the overview or details of either the average root comments or replies per unique user
        else {
            // if one of the product is checked
            const checkedCommentType = updatedLegends.filter(legend => legend.checked && legend.avgName).map(legend => legend.avgName);
            if(checkedCommentType.length === 1) {
                updatedLegends[avgIndex] = {
                    ...legends[avgIndex],
                    value: showAggregate ? overviewDetail?.[checkedCommentType] : selectedDetail?.[checkedCommentType],
                    changeRate: showAggregate ? overviewDetail?.[`${checkedCommentType}RateChange`] : selectedDetail?.[`${checkedCommentType}RateChange`]
                }
            } 
        }

        return updatedLegends[avgIndex];
    }

    const renderBarChartLegends = () => {
        const totalValue = computeTableTotal(chartLegends, false);

        return chartLegends.map((legend, index) => {
            const productPercentage = totalValue > 0 ? ((legend.value ? legend.value : 0) / totalValue) * 100 : 0;
            const productPercentageText = totalValue ? '(' + formatNumber(productPercentage, 'percent') + ')' : '';
            return (
                <ChartLegend
                    title={`${
                        legend.id === 'total' ? 'Total Comments' : legend.name
                    } ${chartLegends.length > 2 && legend.id !== 'total' && legend.id !== 'avgCommentsPerUniqueUser' ? productPercentageText : ''}`}
                    checkboxColor={legend.color}
                    checked={legend.checked}
                    onCheck={checked => {
                        const legends = [...chartLegends];
                        legends[index] = {
                            ...legends[index],
                            checked: checked
                        }

                        legends[totalIndex] = {
                            ...legends[totalIndex],
                            value: computeTableTotal(legends)
                        }

                        legends[avgIndex] = {
                            ...legends[avgIndex], 
                            ...computeAvg({legends, selectedDate})
                        }

                        setChartLegends(legends);
                    }}
                    value={formatNumber(legend.value, 'number')}
                    changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                    change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                    changeType={changeType}
                    metricType={'commenting'}
                />
            )
        })        
    }

    const renderTotalViewsBarChartLegends = () => {
      return totalViewsChartLegends.map((legend, index) => {
          return (
              <ChartLegend
                  title={legend.name}
                  checkboxColor={legend.color}
                  checked={legend.checked}
                  onCheck={checked => {
                      const legends = [...totalViewsChartLegends];
                      legends[index] = {
                          ...legends[index],
                          checked: checked
                      }        

                      setTotalViewsChartLegends(legends)
                  }}
                  value={formatNumber(legend.value, 'number')}
                  changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                  change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                  changeType={changeType}
                  metricType={'commenting'}
              />
          )
      })        
  }


    const transformBarChartData = () => {
        let barChartData = [];
        barChartData = Object.keys(dates).map((date, index) => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = {date: formattedDate}
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(product => product.checked === true);
                if(checkedProducts.map(product => product.id).indexOf(product.id) >= 0) {
                    const checkedCommentType = chartLegends.filter(legend => legend.checked && legend.avgName).map(legend => legend.avgName);
                    const showAggregateAvg = chartLegends.filter(legend => legend.checked).length === chartLegends.length;
                    const selectedCommentType = showAggregateAvg ? 'avgCommentsPerUniqueUser' : (checkedCommentType.length === 1 ? checkedCommentType : 'avgCommentsPerUniqueUser')
                    if(product.id === 'avgCommentsPerUniqueUser') 
                        item['z'] = detailsDateData[selectedCommentType]
                    else {
                        item[product.name] = detailsDateData[product.id];
                        item[`${product.name}Color`] = product.color;
                    }
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }

    const transformTotalViewsBarChartData = () => {
      let barChartData = [];
      barChartData = Object.keys(totalViewsDates).map((date, index) => {
          const dateLength = Object.keys(totalViewsDates).length;
          const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
          let item = {date: formattedDate}
          totalViewsProducts.forEach(product => {
              const detailsDateData = totalViewsDetails[selectedInterval][date];
              const checkedProducts = totalViewsChartLegends.filter(product => product.checked === true);
           
      
              if(checkedProducts.map(product => product.id).indexOf(product.id) >= 0) {
                  item[product.name] = detailsDateData[product.id];
                  item[`${product.name}Color`] = product.color;
               
                  item['origDate'] = date;
                  item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
              }

          })
         
          return item;
      })

      return barChartData;
  }

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
            if(productDetails) {
                value = productDetails[product.id] ? productDetails[product.id] : 0;
                changeRate = productDetails[`${product.id}RateChange`]  ? productDetails[`${product.id}RateChange`] : 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        legends[totalIndex] = {
            ...legends[totalIndex],
            value: computeTableTotal(legends)
        }

        legends[avgIndex] = {
            ...legends[avgIndex], 
            ...computeAvg({legends, selectedDate: date, showTotalAvg: false})
        }

        setSelectedDate(date);
        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
    }

    const handleTotalViewsHover = barData => {
      const date = barData.data ? barData.data.origDate : barData.origDate;
      const legends = [...totalViewsChartLegends];

      totalViewsProducts.forEach(product => {
          let value = 0;
          let changeRate = 0;
          const productDetails = totalViewsDetails[selectedInterval][date] ? totalViewsDetails[selectedInterval][date] : null;
          if(productDetails) {
              value = productDetails[product.id] ? productDetails[product.id] : 0;
              changeRate = productDetails[`${product.id}ChangeRate`]  ? productDetails[`${product.id}ChangeRate`] : 0;
          }

          const legendIndex = totalViewsChartLegends.map(legend => legend.id).indexOf(product.id);
          legends[legendIndex] = {
              ...legends[legendIndex],
              value,
              changeRate
          }
      })
      
      setSelectedDate(date);
      setTotalViewsChartLegends(legends)
      setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
      setShowTotal(false);
  }


    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedDate(null);
        setSelectedPeriodX('');
    }

    const isTotalCommentsSelected = selectedReport === 'total-comments'


    const monetizationBarChart = {
        indexBy: 'date',
        data: isTotalCommentsSelected ? transformBarChartData() : transformTotalViewsBarChartData(),
        keys: getBarChartKeys(),
        colors: getBarChartColors(),
        legends: isTotalCommentsSelected? renderBarChartLegends() : renderTotalViewsBarChartLegends()
    }

    const handleSelectReport = report => {
      loadReport(true)
      selectReport(report)
      timerRef.current = setTimeout(() => loadReport(false), 1500);
    }

    const cardtoggles = (
		<CardToggles>
            <CardToggle
                label={'Total Comments'}
                compareType={changeType}
                metricType={'commenting'}
                percentChange={{
                    value: overview?.period ? formatNumber(overview.period.totalRateChange, 'percent') : 0,
                    direction: overview?.period ? getChangeRateDirection(overview.period.totalRateChange) : 'up'
                }}
                selected={selectedReport === 'total-comments'}
                handleClick={() => handleSelectReport('total-comments')}
                value={overview?.period ? formatNumber(overview.period.total) : ''}
            />
            <CardToggle
                label={'Total Views'}
                compareType={changeType}
                metricType={'commenting'}
                percentChange={{
                    value: totalViewsOverview?.period ? formatNumber(totalViewsOverview.period.totalViewsChangeRate, 'percent') : 0,
                    direction: totalViewsOverview?.period ? getChangeRateDirection(totalViewsOverview.period.totalViewsChangeRate) : 'up'
                }}
                selected={selectedReport === 'total-commenting-views'}
                handleClick={() => handleSelectReport('total-commenting-views')}
                value={totalViewsOverview?.period ? formatNumber(totalViewsOverview.period.totalViews) : ''}
            />
		</CardToggles>
	);

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={monetizationBarChart.colors}
            keys={monetizationBarChart.keys}
            data={monetizationBarChart.data}
            period={selectedInterval}
            loading={status === 'pending' || isLoadingReport ? true : false}
            onHover={data => isTotalCommentsSelected ? handleHoverOnBarChart(data) : handleTotalViewsHover(data)}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            zAxisId={isTotalCommentsSelected ? 'avgCommentsPerUniqueUser' : null}
            zAxisColor={chartZAxisColor}
        />
    );

  const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'commenting'}
            products={isTotalCommentsSelected ? products : totalViewsProducts}
            data={isTotalCommentsSelected  ? (details ? details[selectedInterval] : null) : (totalViewsDetails ? totalViewsDetails[selectedInterval] : null)}
            csvFileName={isTotalCommentsSelected ? 'Total Comments Report' : 'Total Commenting Views Report'}
        />
    ) : []

    const isNoData = () => {
        return  (status === 'rejected') ||
                (status === 'fulfilled' && !overview?.period?.total) ||
                (status === 'fulfilled' && Object.keys(overview).length === 0);
    }

    return (
        <div name={'totalcommentsreport'} class={`${props.classes.totalcommentsreport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={'Total Comments/Views'}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>{ isTotalCommentsSelected ? 'Total Comments' : 'Total Views' }</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>
                              { isTotalCommentsSelected ?
                              'The Total Comments report includes counts of root comments, original comments from users, and replies. The Average Comments per Unique User is calculated by taking the total comments (both root and replies) and dividing by users who made at least one comment during the rolled-up time period'
                              :
                              'The Total Views metric indicates the number of times this commenting unit has been visible in the users viewport.'
                              }
                            </p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={barchart}
                note={!isTotalCommentsSelected && <span><strong>Note:</strong> Device and Country filters do not apply to Total Views data</span>}
                loading={status === 'pending' ? true : false}
                noData={isNoData()}
                enabledPeriods={enabledRollups}
                chartLegend={monetizationBarChart.legends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default TotalCommentsReport;
